'use client';

import { c as _c } from "react/compiler-runtime";
import { createBackerRewardScope, type ItemsRenderProps, useGetBackerRewards } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { InfiniteList } from '@/components/ui/infinite-list';
import { Link } from '@/i18n/navigation';
import { PORTFOLIO_BACKED_PROPOSAL_FIELDS } from '@/lib/sdk/projections';
import { logger } from '@/lib/telemetry/logger';
import { PortfolioListTransition } from './portfolio-list-transition';
import { PortfolioProposalCreators } from './portfolio-proposal-creators';
import { PortfolioProposalListItem } from './portfolio-proposal-list-item';
interface PortfolioProposalPositionsProps {
  userId: string;
  creatorUserId: string;
  walletPubkeys: readonly string[];
  own: boolean;
  tab: 'open' | 'closed';
  renderControls: (count: number | undefined) => ReactNode;
}
export function PortfolioProposalPositions(t0) {
  const $ = _c(27);
  const {
    userId,
    creatorUserId,
    walletPubkeys,
    own,
    tab,
    renderControls
  } = t0;
  const t = useTranslations("profile.backedProposals");
  let t1;
  if ($[0] !== userId || $[1] !== walletPubkeys) {
    t1 = createBackerRewardScope(userId, walletPubkeys);
    $[0] = userId;
    $[1] = walletPubkeys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== walletPubkeys) {
    t2 = walletPubkeys.join(",");
    $[3] = walletPubkeys;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== userId) {
    t3 = {
      userId,
      rewardScope: t1,
      walletPubkeysCsv: t2,
      sortBy: "backed",
      fields: PORTFOLIO_BACKED_PROPOSAL_FIELDS
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = userId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = userId.length > 0 && walletPubkeys.length > 0;
  let t5;
  if ($[9] !== t4) {
    t5 = {
      enabled: t4
    };
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const query = useGetBackerRewards(t3, t5);
  const failed = query.isError;
  const visibleRows = query.isPlaceholderData ? [] : query.rows.filter(row => tab === "open" ? row.marketState === "created" || row.marketState === "backing" || row.marketState === "trading" : row.marketState === "resolved" || row.marketState === "graduated");
  const visibleIds = new Set(visibleRows.map(_temp));
  let t6;
  let t7;
  if ($[11] !== failed || $[12] !== own || $[13] !== walletPubkeys.length) {
    t6 = () => {
      if (failed) {
        logger.warn("portfolio.proposal_positions.load_failed", {
          walletCount: walletPubkeys.length,
          viewerMode: own ? "own" : "other"
        });
      }
    };
    t7 = [failed, walletPubkeys.length, own];
    $[11] = failed;
    $[12] = own;
    $[13] = walletPubkeys.length;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  let t8;
  if ($[16] !== query || $[17] !== walletPubkeys.length) {
    t8 = async function loadMore() {
      try {
        if (query.nextPageError !== null) {
          await query.retryNextPage();
        } else {
          await query.fetchNextPage();
        }
      } catch {
        logger.warn("portfolio.proposal_positions.page_failed", {
          walletCount: walletPubkeys.length
        });
      }
    };
    $[16] = query;
    $[17] = walletPubkeys.length;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const loadMore = t8;
  let t9;
  if ($[19] !== query) {
    t9 = function retry() {
      query.refetch();
    };
    $[19] = query;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const retry = t9;
  const t10 = renderControls(query.isPending || failed || query.isPlaceholderData ? undefined : visibleRows.length);
  const t11 = `proposals:${tab}`;
  const t12 = query.isPending ? <output aria-label={t("loading")} className="flex flex-col gap-2"><Skeleton className="h-24 w-full rounded-card" /><Skeleton className="h-24 w-full rounded-card" /></output> : failed ? <EmptyState size="compact" title={t("error")} action={<AnimatedButton variant="secondary" onClick={retry}>{t("retry")}</AnimatedButton>} /> : visibleRows.length === 0 && !query.hasNextPage ? <EmptyState size="compact" title={t("emptyTitle")} description={own ? t("emptyOwn") : t("emptyOther")} action={own ? <AnimatedButton variant="secondary" asChild={true}><Link href="/proposals">{t("browse")}</Link></AnimatedButton> : undefined} /> : <><InfiniteList hasNextPage={query.hasNextPage && query.nextPageError === null} isFetchingNextPage={query.isFetchingNextPage} fetchNextPage={loadMore} scrollMode="page-below-lg"><PortfolioProposalCreators userId={creatorUserId} pubkeys={[...visibleIds]}>{createdIds => <ul className="flex flex-col"><query.Items queryKey={query.queryKey}>{t13 => {
              const {
                id,
                ref
              } = t13;
              return visibleIds.has(id) ? <li key={id} ref={ref} className="mb-4 min-w-0 lg:mb-0"><PortfolioProposalListItem pubkey={id} isCreator={createdIds.has(id)} /></li> : null;
            }}</query.Items></ul>}</PortfolioProposalCreators></InfiniteList>{query.nextPageError !== null ? <p role="alert" className="text-caption text-foreground-secondary">{t("error")}</p> : null}{query.hasNextPage ? <div className="flex justify-center"><AnimatedButton variant="secondary" loading={query.isFetchingNextPage} disabled={query.isFetchingNextPage} onClick={() => void loadMore()}>{query.nextPageError !== null ? t("retry") : t("loadMore")}</AnimatedButton></div> : null}</>;
  let t14;
  if ($[21] !== t11 || $[22] !== t12) {
    t14 = <PortfolioListTransition view={t11}>{t12}</PortfolioListTransition>;
    $[21] = t11;
    $[22] = t12;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t10 || $[25] !== t14) {
    t15 = <>{t10}{t14}</>;
    $[24] = t10;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  return t15;
}
function _temp(row_0) {
  return row_0.marketPubkey;
}